import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import {
  GridDataResult,
  SelectableSettings
} from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor, State } from '@progress/kendo-data-query';
import { formatDate } from '@telerik/kendo-intl';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ResulteableComponent } from '../../../../shared/components/interfaces/resulteable.component';
import { GridSettings } from '../../../../shared/model/grid-settings.interface';
import { AuthService } from '../../../security/service/auth.service';
import { AssetRoutingStates } from '../../asset-routing.module';
import { AssetService } from '../../service/asset.service';
import { AssetListResultColumnsModule } from '../model/asset-list-result-columns.module';

@Component({
  selector: 'asset-list-result',
  templateUrl:
    './console/app-js/modules/asset/components/asset-list-results/asset-list-results.component.html'
})
export class AssetListResultComponent extends APIOperableComponent
  implements OnChanges, ResulteableComponent {
  // Variable declarations
  @Output() deactivateEvent = new EventEmitter<any>();
  @Output() activateEvent = new EventEmitter<any>();
  @Output() pageEvent = new EventEmitter<any>();
  @Input('data') data: any[];
  @Input('loadingIndicator') loadingIndicator: boolean;
  @Input('noResults') noResults: boolean;
  @ViewChild('vixxoGrid', { static: true }) vixxoGrid: any;

  public gridView: GridDataResult;
  public sort: SortDescriptor[] = [];
  protected listOfSelectedObjects = [];
  protected state: State;
  protected assetData = [];
  noRecordsSettings = {
    message: '',
    styleClass: 'no-records-wrapper'
  };
  columns = AssetListResultColumnsModule;
  gridSettings: GridSettings = {
    state: {
      skip: 0,
      take: 5,
      filter: {
        logic: 'and',
        filters: []
      }
    },
    columnsConfig: this.columns,
    realColWidths: []
  };
  selectableSettings: SelectableSettings = {
    enabled: true,
    checkboxOnly: false,
    mode: 'multiple'
  };

  constructor(
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private router: Router,
    private assetService: AssetService
  ) {
    super(progressService, toastr, authService);
    const gridSettings: GridSettings = this.assetService.getStatePersistingInputs();
    if (gridSettings) {
      this.gridSettings = this.mapGridSettings(gridSettings);
    }
    this.loadAssets();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.loadAssets();
  }

  private loadAssets(): void {
    if (this.data !== undefined && this.data.length !== 0) {
      this.assetData = JSON.parse(JSON.stringify(this.data));
      this.assetData.forEach(obj => {
        obj.serialNumber = `<a class="cellLink"><span>${
          obj.serialNumber ? obj.serialNumber : ''
        }</span></a>`;
        obj.createdDate = `<span> ${
          obj.createdDate != null
            ? formatDate(new Date(obj.createdDate), 'MM/dd/yyyy')
            : ''
        }</span>`;
        obj.updatedDate = !obj.updatedDate
          ? ''
          : `<span> ${formatDate(
              new Date(obj.updatedDate),
              'MM/dd/yyyy'
            )}</span>`;
        obj.isActive = obj.isActive === true ? 'Active' : 'Inactive';
        if (obj.assetType != undefined && obj.assetType !== null) {
          obj['assetType'] = `<span class="badge">${obj.assetType}</span>`;
        } else {
          obj['assetType'] = `<span class="badge">${''}</span>`;
        }
      });
      this.gridView = {
        data: orderBy(this.assetData, this.sort),
        total: this.assetData.length
      };
    } else {
      this.assetData = [];
      if (this.noResults) {
        this.noRecordsSettings.message = 'No Results Found';
        this.noRecordsSettings.styleClass = null;
      }
    }
    // Set Kendo Grid Columns to user previous used.
    if (this.gridSettings.realColWidths.length > 0) {
      setTimeout(
        () =>
          this.vixxoGrid.setGridColumnWidths(this.gridSettings.realColWidths),
        300
      );
    }
  }

  public cellClickHandler({ dataItem, column }) {
    if (column.field === 'serialNumber') {
      this.onItemDetail(dataItem.id);
    }
  }

  /**
   * Implementation from OnChanges interface
   * On every change we clean the current selection of values.
   */
  public ngOnChanges(): void {
    this.deactivateEvent.next([]);
    this.activateEvent.next([]);
    this.listOfSelectedObjects = [];
    this.loadAssets();
  }

  /**
   * Take the user to the Details.
   * @param item
   */
  onItemDetail(id: any): void {
    this.router.navigate([
      `/console/${AssetRoutingStates.LIST.url}`,
      id,
      'view'
    ]);
  }

  onExpandRow(row: any): void {
    // Not yet implemented on this module.
  }

  onCollapseRow(): void {
    // Not yet implemented on this module.
  }

  /**
   * Get the entire Row class
   * @param row
   */
  protected getRowClass(row: any): any {
    const inActive = row.dataItem.isActive === 'Active' ? true : false;
    return {
      'inactive-row': !inActive
    };
  }

  /**
   * On sort event occurred.
   * We manually set the current sort event to the grid sorts.
   * @param event
   */
  onSort(event) {
    event.sorts = [{ prop: event.column.prop, dir: event.newValue }];
  }

  /**
   * Selects an item by clicking the checkbox from the list
   *
   * @param event
   */
  selectedRowChange(event) {
    let activeSelectedItems = [];
    let inactiveSelectedItems = [];
    this.searchForArray(this.listOfSelectedObjects, event.deselectedRows);

    for (let i = 0; i < event.selectedRows.length; i++) {
      this.listOfSelectedObjects.push(event.selectedRows[i]);
    }
    let dataItem = [];
    for (let i = 0; i < this.listOfSelectedObjects.length; i++) {
      dataItem = this.data.find(element => {
        return this.listOfSelectedObjects[i].dataItem.id === element.id;
      });
      if (this.listOfSelectedObjects[i].dataItem.isActive === 'Active') {
        activeSelectedItems.push(dataItem);
      } else {
        inactiveSelectedItems.push(dataItem);
      }
    }
    this.deactivateEvent.next(activeSelectedItems);
    this.activateEvent.next(inactiveSelectedItems);
  }

  searchForArray(selectedItems, deselectedItmes) {
    for (let i = 0; i < selectedItems.length; i++) {
      for (let j = 0; j < deselectedItmes.length; j++) {
        if (selectedItems[i].dataItem === deselectedItmes[j].dataItem) {
          selectedItems.splice(i, 1);
        }
      }
    }
  }

  /**
   * Reset column widths and delete current custom widths.
   */
  public appliedColumnOrder(e: any) {
    this.gridSettings.columnsConfig = e;
    this.gridSettings.realColWidths = [];
    this.saveGrid();
  }

  /**
   * Get the current column widths and save them.
   */
  public columnResizeEvent(e: any) {
    this.gridSettings.realColWidths = this.vixxoGrid
      .getKendoGridInstance()
      .columns.filter(item => item.isVisible)
      .map(item => item.width);
    this.saveGrid();
  }

  /**
   * Arguments for the dataStateChange event
   * @param state
   */
  public dataStateChange(state: State) {
    this.gridSettings.state = state;
    this.saveGrid();
  }

  /**
   *
   * @param gridSettings custom GridSettings object by using custom logic.
   */
  public mapGridSettings(gridSettings: GridSettings): GridSettings {
    const state = gridSettings.state;
    return {
      state,
      columnsConfig: gridSettings.columnsConfig,
      realColWidths:
        gridSettings.realColWidths && gridSettings.realColWidths.length > 0
          ? gridSettings.realColWidths
          : []
    };
  }
  /**
   * save grid
   */
  private saveGrid(): void {
    const gridConfig = {
      columnsConfig: this.gridSettings.columnsConfig,
      state: this.gridSettings.state,
      realColWidths: this.gridSettings.realColWidths
    };
    this.assetService.setStatePersistingInputs(gridConfig);
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    this.pageEvent.emit();
  }
}
